/**
 * revenuecat.js — thin JS wrapper around @revenuecat/purchases-capacitor.
 *
 * FLYT tier map (matches App Store Connect product IDs):
 *   com.flytstudios.league.athlete.monthly           $4.99  — FLYT ID™ link
 *   com.flytstudios.league.athlete.annual            $49.99 — 2 months free
 *   com.flytstudios.league.agent.solo.monthly        $9.99  — Agent + 4 athletes
 *   com.flytstudios.league.agent.solo.annual         $99.99 — 2 months free
 *   com.flytstudios.league.program.team10.monthly    $49.99 — Program up to 10
 *   com.flytstudios.league.program.team10.annual.v2  $499.99  (v2: original .annual ID
 *                                                     was burned by deletion — Apple
 *                                                     never releases product IDs)
 *   com.flytstudios.league.program.team20.monthly    $59.99 — Program up to 20
 *   com.flytstudios.league.program.team20.annual     $599.99
 *   com.flytstudios.league.program.team30.monthly    $79.99 — Program up to 30
 *   com.flytstudios.league.program.team30.annual     $799.99
 *
 * Every product unlocks one of these RevenueCat entitlements:
 *   athlete_premium · agent_solo · program_team10 · program_team20 · program_team30
 *
 * All products have a 7-day free trial configured as an Introductory Offer
 * in App Store Connect. Apple enforces one-per-subscription-group so a
 * user upgrading Athlete→Agent within the same group doesn't retrigger it.
 */
import { Capacitor } from '@capacitor/core';
import axios from 'axios';

const RC_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || '';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let _configured = false;

/**
 * iter766 · Jay found a real case: Apple's own subscription sheet showed
 * an active renewal, but the app's own billing page still read Free.
 * The server-to-server webhook is keyed on RevenueCat's `app_user_id`,
 * which can drift from our own `user_id` (an anonymous-ID race before
 * login resolves, an account switch without a clean RC logout, a
 * reinstall) — when it does, the webhook's write silently lands on the
 * wrong account or on none. `entitlementInfo` here always comes straight
 * from RevenueCat's own servers (already validated against Apple), never
 * something the client made up, so pushing it to OUR backend — scoped to
 * whichever account is actually authenticated on THIS device right now —
 * gives every purchase/restore/launch a second, account-safe path to the
 * same effect. Best-effort: never throws, never blocks the caller.
 */
async function _syncEntitlementToBackend(entitlementInfo) {
  if (!entitlementInfo?.identifier || !entitlementInfo?.expirationDate) return;
  try {
    await axios.post(`${API}/me/premium/sync-apple`, {
      entitlement_id: entitlementInfo.identifier,
      expires_at: entitlementInfo.expirationDate,
    }, { withCredentials: true });
  } catch (e) {
    console.warn('[RevenueCat] backend sync failed:', e?.message || e);
  }
}

export const ENTITLEMENT_IDS = [
  'athlete_premium',
  'agent_solo',
  'program_team10',
  'program_team20',
  'program_team30',
  // iter780 · Media Hub storage add-on tiers — purchasable independently
  // of the premium tiers above. Keep in sync with
  // backend/server.py#_RC_ENTITLEMENT_TO_MEDIA_PLAN.
  'media_storage_5gb',
  'media_storage_10gb',
  'media_storage_15gb',
  'media_storage_20gb',
  'media_storage_50gb',
  'media_storage_100gb',
];
// iter784 · Jay: buying a storage tier (e.g. 5 GB, $9.99) got recorded
// on the backend as his existing $4.99 Athlete Premium subscription
// instead. Root cause — every entitlement-detection loop below (and
// the old body of `purchaseProductById`) scanned `ENTITLEMENT_IDS` in
// this fixed priority order and returned the FIRST one it found
// active. All the storage tiers sit in the SAME App Store subscription
// group as Athlete Premium (see revenuecat.js header + backend
// comments), so during the moment right after a crossgrade purchase
// it's possible for RevenueCat's cached CustomerInfo to still list the
// old entitlement as active alongside the new one — and since
// `athlete_premium` is listed before any `media_storage_*` id, the old
// one always won, even though the person just paid for a specific GB
// tier. `purchaseProductById` now looks up the ONE entitlement that
// product is actually supposed to grant and checks that FIRST, so a
// purchase is always reported (and synced to the backend) as what was
// actually just bought.
const PRODUCT_TO_ENTITLEMENT = {
  'com.flytstudios.league.athlete.monthly': 'athlete_premium',
  'com.flytstudios.league.athlete.annual': 'athlete_premium',
  'com.flytstudios.league.agent.solo.monthly': 'agent_solo',
  'com.flytstudios.league.agent.solo.annual': 'agent_solo',
  'com.flytstudios.league.program.team10.monthly': 'program_team10',
  'com.flytstudios.league.program.team10.annual.v2': 'program_team10',
  'com.flytstudios.league.program.team20.monthly': 'program_team20',
  'com.flytstudios.league.program.team20.annual': 'program_team20',
  'com.flytstudios.league.program.team30.monthly': 'program_team30',
  'com.flytstudios.league.program.team30.annual': 'program_team30',
  'com.flytstudios.league.storage.5gb.monthly': 'media_storage_5gb',
  'com.flytstudios.league.storage.10gb.monthly': 'media_storage_10gb',
  'com.flytstudios.league.storage.15gb.monthly': 'media_storage_15gb',
  'com.flytstudios.league.storage.20gb.monthly': 'media_storage_20gb',
  'com.flytstudios.league.storage.50gb.monthly': 'media_storage_50gb',
  'com.flytstudios.league.storage.100gb.monthly': 'media_storage_100gb',
};

/** True when we're on iOS AND the SDK key is present. */
export function isRevenueCatAvailable() {
  return Capacitor.getPlatform() === 'ios' && !!RC_KEY;
}

/**
 * Configure the SDK. Call once per app boot AFTER the user is signed in
 * so `appUserID` matches our own `public_users.user_id` — that's the
 * key we use to match webhook events back to the Mongo user row.
 */
export async function configureRevenueCat(appUserID) {
  if (!isRevenueCatAvailable()) return;
  if (_configured) {
    // iter872 · If the SDK was configured before sign-in finished (no
    // appUserID → anonymous RevenueCat id), purchases were filed under
    // that anonymous id and the server webhook couldn't find the account.
    // Once we know who's signed in, log RevenueCat in as them — RC links
    // (aliases) the anonymous purchases onto this user automatically.
    if (appUserID) {
      try {
        const { Purchases } = await import('@revenuecat/purchases-capacitor');
        const { appUserID: current } = await Purchases.getAppUserID();
        if (current !== appUserID) await Purchases.logIn({ appUserID });
      } catch (e) {
        console.warn('[RevenueCat] logIn failed:', e?.message || e);
      }
    }
    return;
  }
  try {
    const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');
    await Purchases.setLogLevel({ level: LOG_LEVEL.INFO });
    await Purchases.configure({
      apiKey: RC_KEY,
      appUserID: appUserID || undefined,
    });
    _configured = true;
  } catch (e) {
    console.warn('[RevenueCat] configure failed:', e?.message || e);
  }
}

/**
 * iter748 · Call on sign-out. `configureRevenueCat` no-ops once
 * `_configured` flips true, so switching FLYT accounts on the same
 * device WITHOUT force-quitting the app left the SDK permanently
 * pinned to whichever account configured it first — every entitlement
 * check after that kept reading the FIRST account's subscription no
 * matter who was actually signed in. `Purchases.logOut()` resets the
 * SDK to a fresh anonymous user before the next login re-configures it
 * with the new appUserID.
 */
export async function logOutRevenueCat() {
  if (!isRevenueCatAvailable() || !_configured) return;
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    await Purchases.logOut();
  } catch (e) {
    console.warn('[RevenueCat] logOut failed:', e?.message || e);
  } finally {
    _configured = false;
  }
}

/** Returns the active entitlement id (`agent_solo`, etc.) or null. */
export async function getActiveEntitlement() {
  if (!isRevenueCatAvailable() || !_configured) return null;
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const { customerInfo } = await Purchases.getCustomerInfo();
    const active = customerInfo?.entitlements?.active || {};
    for (const id of ENTITLEMENT_IDS) {
      if (active[id]?.isActive) return id;
    }
    return null;
  } catch (e) {
    console.warn('[RevenueCat] getCustomerInfo failed:', e?.message || e);
    return null;
  }
}

/**
 * iter766 · Call once per app launch, right after `configureRevenueCat`
 * resolves. Reads whatever entitlement RevenueCat already has cached for
 * this account and re-syncs it to our backend — the same self-heal
 * `purchasePackage`/`restorePurchases` do, just without requiring the
 * person to take any action. Fixes an existing account/webhook mismatch
 * (like Jay hit) automatically on the person's next launch, instead of
 * needing them to know to tap "Restore Purchases."
 */
export async function syncCurrentEntitlement() {
  if (!isRevenueCatAvailable() || !_configured) return;
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const { customerInfo } = await Purchases.getCustomerInfo();
    const active = customerInfo?.entitlements?.active || {};
    // iter809 · Jay: an account holding a real Premium tier AND a
    // purchased storage tier at the same time (the exact case
    // `_effective_storage_limit`'s iter808 fix handles on the backend)
    // still showed "Unlimited storage" after the fix shipped — because
    // this loop `return`ed after syncing the FIRST active entitlement
    // it found, and `athlete_premium`/etc. are listed in ENTITLEMENT_IDS
    // ahead of any `media_storage_*` id. The media entitlement was
    // simply never sent to `/me/premium/sync-apple` on launch, so
    // `media_plan` stayed stale (or free) no matter how many times the
    // app relaunched. Sync every active entitlement that matters here,
    // not just the first — `sync-apple` already applies a premium tier
    // and a media tier independently without clobbering each other.
    for (const id of ENTITLEMENT_IDS) {
      if (active[id]?.isActive) {
        await _syncEntitlementToBackend(active[id]);
      }
    }
  } catch (e) {
    console.warn('[RevenueCat] syncCurrentEntitlement failed:', e?.message || e);
  }
}

/**
 * iter874 · Localized App Store price strings for raw product ids
 * ({ productId: '$9.99' } — or '9,99 €' etc. in other storefronts), so
 * paywalls display exactly what Apple will charge (App Review 3.1.2).
 * Returns {} when unavailable; callers fall back to their own prices.
 */
export async function getStorePrices(productIds) {
  if (!isRevenueCatAvailable() || !_configured || !productIds?.length) return {};
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const { products } = await Purchases.getProducts({ productIdentifiers: productIds });
    const out = {};
    (products || []).forEach((p) => { if (p?.identifier && p?.priceString) out[p.identifier] = p.priceString; });
    return out;
  } catch (e) {
    console.warn('[RevenueCat] getProducts failed:', e?.message || e);
    return {};
  }
}

/** Returns the full list of packages from the default offering. */
export async function getAvailablePackages() {
  if (!isRevenueCatAvailable() || !_configured) return [];
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const { current } = await Purchases.getOfferings();
    return current?.availablePackages || [];
  } catch (e) {
    console.warn('[RevenueCat] getOfferings failed:', e?.message || e);
    return [];
  }
}

/**
 * iter546 · Detect user-cancelled purchase across every shape the
 * Capacitor RC plugin returns:
 *   - `e.userCancelled === true`             (documented flag)
 *   - `e.code === 1` or `'PURCHASE_CANCELLED'`  (RC error codes)
 *   - `/cancel/i` in the message string       (fallback)
 * The plugin's marshaling drops `userCancelled` intermittently
 * (build-540 → 543 all showed a red "Purchase failed: Purchase was
 * cancelled" toast because none of the flags survived the bridge).
 */
function isUserCancelled(e) {
  if (!e) return false;
  if (e.userCancelled === true) return true;
  if (e.code === 1 || e.code === '1' || e.code === 'PURCHASE_CANCELLED') return true;
  const msg = String(e.message || e || '').toLowerCase();
  return msg.includes('cancel');
}

/**
 * Trigger the native Apple StoreKit purchase sheet for a package.
 * Returns `{ entitlement, cancelled }`. Never throws — cancellation
 * is a normal outcome, not an error.
 */
export async function purchasePackage(pkg) {
  if (!isRevenueCatAvailable() || !_configured) {
    return { entitlement: null, cancelled: false, error: 'not_available' };
  }
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const res = await Purchases.purchasePackage({ aPackage: pkg });
    const active = res.customerInfo?.entitlements?.active || {};
    // iter784 · Same fix as purchaseProductById — check the entitlement
    // THIS package's underlying product is supposed to grant first. A
    // package's RC identifier ($rc_monthly etc.) is opaque, but its raw
    // App Store product id (pkg.product / pkg.storeProduct, depending on
    // plugin version) maps to exactly one entitlement, so this is just
    // as reliable as the productId-based lookup in purchaseProductById.
    const rawProductId = pkg?.product?.identifier || pkg?.storeProduct?.identifier;
    const expected = rawProductId && PRODUCT_TO_ENTITLEMENT[rawProductId];
    if (expected && active[expected]?.isActive) {
      await _syncEntitlementToBackend(active[expected]);
      return { entitlement: expected, cancelled: false };
    }
    for (const id of ENTITLEMENT_IDS) {
      if (active[id]?.isActive) {
        await _syncEntitlementToBackend(active[id]);
        return { entitlement: id, cancelled: false };
      }
    }
    return { entitlement: null, cancelled: false };
  } catch (e) {
    if (isUserCancelled(e)) return { entitlement: null, cancelled: true };
    console.warn('[RevenueCat] purchase failed:', e?.message || e);
    return { entitlement: null, cancelled: false, error: e?.message || 'unknown' };
  }
}

/** User-triggered restore (surface behind a "Restore Purchases" button). */
export async function restorePurchases() {
  if (!isRevenueCatAvailable() || !_configured) return { entitlement: null };
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const res = await Purchases.restorePurchases();
    const active = res.customerInfo?.entitlements?.active || {};
    // iter809 · Same fix as syncCurrentEntitlement — a restore on an
    // account holding both a Premium tier and a storage tier must sync
    // BOTH to the backend, not stop at the first ENTITLEMENT_IDS match.
    // `entitlement` below (first match, for the caller's toast copy)
    // stays as-is so existing callers keep their current behavior.
    let first = null;
    for (const id of ENTITLEMENT_IDS) {
      if (active[id]?.isActive) {
        await _syncEntitlementToBackend(active[id]);
        if (!first) first = id;
      }
    }
    return { entitlement: first };
  } catch (e) {
    console.warn('[RevenueCat] restore failed:', e?.message || e);
    return { entitlement: null, error: e?.message };
  }
}

/**
 * iter542 · Direct product purchase fallback. When the RC "Current"
 * offering isn't populated yet (products still `In Review` in ASC, or
 * the offering hasn't been set as Current on the RC dashboard) the
 * primary package flow returns nothing. This fallback fetches the raw
 * StoreProduct by identifier and hands it to `purchaseStoreProduct` —
 * bypasses offerings entirely. RC still records the transaction and
 * fires the webhook the same way.
 *
 * Returns `{ entitlement, cancelled, error }` mirroring purchasePackage
 * so callers can treat both paths identically.
 */
export async function purchaseProductById(productId) {
  if (!isRevenueCatAvailable() || !_configured) {
    return { entitlement: null, cancelled: false, error: 'not_available' };
  }
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    // Grab the raw StoreProduct so we can call purchaseStoreProduct.
    const { products } = await Purchases.getProducts({
      productIdentifiers: [productId],
    });
    if (!Array.isArray(products) || products.length === 0) {
      return {
        entitlement: null,
        cancelled: false,
        error: `Product ${productId} not found in App Store Connect — check ASC status and Sandbox Tester storefront.`,
      };
    }
    const product = products[0];
    const res = await Purchases.purchaseStoreProduct({ product });
    const active = res.customerInfo?.entitlements?.active || {};
    // iter784 · Check the entitlement THIS product is actually supposed
    // to grant first — see the PRODUCT_TO_ENTITLEMENT comment above.
    const expected = PRODUCT_TO_ENTITLEMENT[productId];
    if (expected && active[expected]?.isActive) {
      await _syncEntitlementToBackend(active[expected]);
      return { entitlement: expected, cancelled: false };
    }
    for (const id of ENTITLEMENT_IDS) {
      if (active[id]?.isActive) {
        await _syncEntitlementToBackend(active[id]);
        return { entitlement: id, cancelled: false };
      }
    }
    return { entitlement: null, cancelled: false };
  } catch (e) {
    if (isUserCancelled(e)) return { entitlement: null, cancelled: true };
    console.warn('[RevenueCat] direct product purchase failed:', e?.message || e);
    return { entitlement: null, cancelled: false, error: e?.message || 'unknown' };
  }
}
